import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AgentMessage,
  AssistantActionResultInput,
  AssistantAskInput,
  AssistantAskReply,
  AssistantMessage,
  AssistantStatus,
  CreateSpaceAgentInput,
  FeatureSuggestion,
  NameSuggestion,
  SpaceAgent,
  SpaceAgentReply,
  SuggestFeaturesInput,
  SuggestNamesInput,
  UpdateSpaceAgentInput,
} from '@itsappy/shared';
import { api } from '@/lib/api';

/**
 * Whether Itsappy AI can answer in this deployment.
 *
 * Every assistant surface waits on this before rendering a control, so a
 * workspace with no key configured sees an explanation instead of a button
 * that fails when pressed. Cached for the session — the answer only changes
 * when the server is redeployed.
 */
export function useAssistantStatus() {
  return useQuery({
    queryKey: ['assistant', 'status'],
    queryFn: () => api.get<AssistantStatus>('/assistant/status'),
    staleTime: Infinity,
  });
}

export function useSuggestNames() {
  return useMutation({
    mutationFn: (body: SuggestNamesInput) =>
      api.post<{ items: NameSuggestion[] }>('/assistant/names', body).then((r) => r.items),
  });
}

export function useSuggestFeatures() {
  return useMutation({
    mutationFn: (body: SuggestFeaturesInput) =>
      api.post<{ items: FeatureSuggestion[] }>('/assistant/features', body).then((r) => r.items),
  });
}

export function useAssistantAsk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AssistantAskInput) => api.post<AssistantAskReply>('/assistant/ask', body),
    // The server writes both turns, so the thread is the record of what was
    // said — refetch rather than reconstructing it here.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assistant', 'thread'] }),
  });
}
/** The caller's own conversation with the general assistant. */
export function useAssistantThread() {
  return useQuery({
    queryKey: ['assistant', 'thread'],
    queryFn: () => api.get<{ items: AssistantMessage[] }>('/assistant/thread').then((r) => r.items),
  });
}

/**
 * Marks a proposed action as run, or waved away.
 *
 * The action itself already went through its own endpoint — this is only what
 * stops the same card from offering to do it again after a reload.
 */
export function useRecordAssistantAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, ...body }: AssistantActionResultInput & { messageId: string }) =>
      api.patch<AssistantMessage>(`/assistant/thread/${messageId}/action`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assistant', 'thread'] }),
  });
}

export function useClearAssistantThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>('/assistant/thread'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assistant', 'thread'] }),
  });
}


/* ----------------------------- space agents ---------------------------- */

export function useSpaceAgents(spaceId: string | undefined) {
  return useQuery({
    queryKey: ['space-agents', spaceId],
    queryFn: () => api.get<{ items: SpaceAgent[] }>(`/spaces/${spaceId}/agents`).then((r) => r.items),
    enabled: Boolean(spaceId),
  });
}

export function useCreateSpaceAgent(spaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSpaceAgentInput) =>
      api.post<SpaceAgent>(`/spaces/${spaceId}/agents`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['space-agents', spaceId] }),
  });
}

export function useUpdateSpaceAgent(spaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, ...body }: UpdateSpaceAgentInput & { agentId: string }) =>
      api.patch<SpaceAgent>(`/spaces/${spaceId}/agents/${agentId}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['space-agents', spaceId] }),
  });
}

export function useDeleteSpaceAgent(spaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => api.delete<void>(`/spaces/${spaceId}/agents/${agentId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['space-agents', spaceId] }),
  });
}

export function useRunSpaceAgent(spaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, ...body }: { agentId: string; task: string; context?: string }) =>
      api.post<SpaceAgentReply>(`/spaces/${spaceId}/agents/${agentId}/run`, body),
    // The server writes both turns, so the thread is the source of truth for
    // what was said — refetch rather than guessing at it locally.
    onSuccess: (_reply, { agentId }) =>
      qc.invalidateQueries({ queryKey: ['agent-thread', spaceId, agentId] }),
  });
}
/**
 * Sends an agent to a room.
 *
 * Nothing is invalidated on success: the walk itself arrives over the socket
 * as ordinary movement, so the space already updates from the same events that
 * move everybody else.
 */
export function useWalkSpaceAgent(spaceId: string) {
  return useMutation({
    mutationFn: ({ agentId, zoneId }: { agentId: string; zoneId: string }) =>
      api.post<{ ok: boolean }>(`/spaces/${spaceId}/agents/${agentId}/walk`, { zoneId }),
  });
}

/**
 * The caller's own conversation with one agent. Kept in the cache under the
 * agent so switching between agents in a space does not refetch the others.
 */
export function useAgentThread(spaceId: string, agentId: string | undefined) {
  return useQuery({
    queryKey: ['agent-thread', spaceId, agentId],
    queryFn: () =>
      api.get<{ items: AgentMessage[] }>(`/spaces/${spaceId}/agents/${agentId}/thread`).then((r) => r.items),
    enabled: Boolean(agentId),
  });
}

export function useClearAgentThread(spaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => api.delete<void>(`/spaces/${spaceId}/agents/${agentId}/thread`),
    onSuccess: (_result, agentId) =>
      qc.invalidateQueries({ queryKey: ['agent-thread', spaceId, agentId] }),
  });
}

